import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const AutReportDetail: React.FC<{
  heatNumber: string;
  pipeLengthM?: number;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, pipeLengthM = 12.18, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const autMeasurements = measurements.filter((measurement) => measurement.reportType === 'AUT');
  const pipeLengthMm = pipeLengthM * 1000;
  const projectLimitRows: Array<[string, React.ReactNode, boolean?]> = projectLimitFields.length
    ? projectLimitFields.map((field) => [field.label, field.limit, true])
    : [
      ['Acceptance level', AUT_DETAIL.acceptanceLevel, true],
      ['Coverage', '= 100 %', true],
      ['Depth max', '<= 10 % WT', true],
      ['Length max', '<= 50 mm', true],
    ];
  const getAutLimitText = (fieldPath: string, fallback: string) =>
    projectLimitFields.find((field) => field.fieldPath === fieldPath)?.limit || fallback;
  const acceptanceLimitText = getAutLimitText('acceptance_level', AUT_DETAIL.acceptanceLevel);
  const coverageLimitText = getAutLimitText('coverage_pct', '= 100 %');
  const depthLimitText = getAutLimitText('indication_depth_pct', '<= 10 % WT');
  const lengthLimitText = getAutLimitText('indication_length_mm', '<= 50 mm');
  const depthLimitMax = getProjectLimitMax(projectLimitFields, 'indication_depth_pct', 10);
  const lengthLimitMax = getProjectLimitMax(projectLimitFields, 'indication_length_mm', 25);
  const coverageLimitMin = getProjectLimitMin(projectLimitFields, 'coverage_pct', 100);
  const indications = autMeasurements.map((measurement) => ({
    id: measurement.autIndicationId || measurement.id,
    flawType: measurement.autFlawType || 'indication',
    posMm: Number(measurement.autPositionMm) || 0,
    clock: measurement.autClock || '3:00',
    depthPct: Number(measurement.autDepthPctWt) || 0,
    lengthMm: Number(measurement.autLengthMm || measurement.measuredValue) || 0,
    disposition: measurement.autDisposition || 'accept',
    status: measurement.status,
    measurement,
  }));
  const coverageMeasurement = autMeasurements.find((measurement) => measurement.fieldPath === 'coverage_pct');
  const parsedCoverage = Number(coverageMeasurement?.measuredValue);
  const reportCoveragePct = Number.isFinite(parsedCoverage) && parsedCoverage > 0
    ? parsedCoverage
    : (indications.length ? 100 : 0);
  const coverageStatus = reportCoveragePct >= coverageLimitMin ? 'PASS' : 'FAIL';
  const [activeIndication, setActiveIndication] = useState<string | null>(null);
  useEffect(() => {
    if (!indications.length) {
      setActiveIndication(null);
      return;
    }
    setActiveIndication((current) => {
      if (current && indications.some((item) => item.id === current)) return current;
      return indications[0].id;
    });
  }, [indications]);
  const indication = indications.find((item) => item.id === activeIndication);
  const acceptedCount = indications.filter((item) => item.status === 'PASS').length;
  const clockToY = (clock: string) => {
    const [hourRaw, minuteRaw = '0'] = clock.split(':');
    const hour = Number(hourRaw) || 0;
    const minute = Number(minuteRaw) || 0;
    const angle = (((hour % 12) + minute / 60) / 12) * 2 * Math.PI;
    return 50 - Math.cos(angle) * 30;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Indications on pipe</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">Acceptance {acceptanceLimitText} - depth {depthLimitText}, length {lengthLimitText}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-[#BBD9F4] bg-[#E7F3FF] px-3 py-1 text-[11px] font-semibold text-blueprint-blue">AUT+TOFD · N2</span>
            <Button type="button" size="sm" onClick={() => onAddMeasurement?.('AUT')} className="bg-hero-red text-white hover:bg-hero-red-60">
              <Plus className="mr-2 h-4 w-4" />
              Add Indication
            </Button>
          </div>
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-4">
            <div className="relative h-24 overflow-hidden rounded-full border border-[#D9DEE6] bg-gradient-to-b from-white via-[#EEF0F3] to-[#E2E5EA] shadow-inner">
              {[0, 0.25, 0.5, 0.75, 1].map((point) => (
                <div key={point} className="absolute bottom-0 top-0 w-px bg-[#D9DEE6]/70" style={{ left: `${point * 100}%` }} />
              ))}
              <div className="absolute left-6 right-6 top-5 h-0.5 bg-[#C8CED8]" />
              <span className="absolute bottom-3 left-5 font-mono text-[10px] text-heritage-grey">End 1</span>
              <span className="absolute bottom-3 right-5 font-mono text-[10px] text-heritage-grey">End 2</span>
              <span className="absolute right-6 top-2 font-mono text-[10px] text-heritage-grey">seam · 12:00</span>
              {indications.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  title={`${item.id} · ${item.flawType} · ${item.depthPct}% x ${item.lengthMm} mm`}
                  onClick={() => setActiveIndication(item.id)}
                  className={cn(
                    'absolute h-2.5 w-2.5 rounded-full border-2 border-white shadow-sm',
                    item.status === 'FAIL' ? 'bg-readable-red' : item.status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : 'bg-galvanizing-green'
                  )}
                  style={{
                    left: `calc(${Math.max(1, Math.min(99, pipeLengthMm > 0 ? (item.posMm / pipeLengthMm) * 100 : 0))}% - 5px)`,
                    top: `calc(${clockToY(item.clock)}% - 5px)`,
                  }}
                />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="font-mono text-[10px] text-heritage-grey">0 mm</span>
              {indications.map((item) => (
                <div key={item.id} className="flex items-center gap-1.5">
                  <span className={cn('h-2 w-2 rounded-full', item.status === 'FAIL' ? 'bg-readable-red' : item.status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : 'bg-galvanizing-green')} />
                  <span className="font-mono text-[10px] text-heritage-grey">{item.id} · {item.flawType}</span>
                </div>
              ))}
              <span className="ml-auto text-[11px] text-heritage-grey">{indications.length} indications · {acceptedCount} accepted</span>
              <span className="font-mono text-[10px] text-heritage-grey">{pipeLengthM.toFixed(2)} m</span>
            </div>
          </CardContent>
        </Card>

        {indication && (
          <Card className="overflow-hidden border-[#E2E5EA]">
            <CardContent className="p-0">
              <SectionHead
                title={indication.id}
                subtitle={`${indication.flawType} / ${indication.clock} / ${(indication.posMm / 1000).toFixed(2)} m from end 1`}
                status={indication.status}
                right={indication.measurement ? (
                  <ItemActionButtons measurement={indication.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                ) : undefined}
              />
              <TensileResultRow label="Depth" formula={depthLimitText} value={indication.depthPct} unit="%" min={0} max={15} hi={depthLimitMax} status={indication.status} />
              <TensileResultRow label="Length" formula={lengthLimitText} value={indication.lengthMm} unit="mm" min={0} max={60} hi={lengthLimitMax} status={indication.status} />
              <TensileResultRow label="Coverage" formula={coverageLimitText} value={reportCoveragePct} unit="%" min={0} max={100} lo={coverageLimitMin} status={coverageStatus} />
            </CardContent>
          </Card>
        )}

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Indication detail" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3 text-right">Pos. (mm)</th>
                  <th className="px-4 py-3">Clock</th>
                  <th className="px-4 py-3">Flaw type</th>
                  <th className="px-4 py-3 text-right">Depth % WT</th>
                  <th className="px-4 py-3 text-right">Length mm</th>
                  <th className="px-4 py-3">Disposition</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {indications.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => setActiveIndication(item.id)}
                    className={cn('cursor-pointer', item.id === activeIndication && 'bg-[#E7F3FF]')}
                  >
                    <td className="px-4 py-3 font-mono font-semibold">{item.id}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.posMm.toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono">{item.clock}</td>
                    <td className="px-4 py-3">{item.flawType}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.depthPct.toFixed(1)} <MiniStatusDot status={item.status} /></td>
                    <td className="px-4 py-3 text-right font-mono">{item.lengthMm.toFixed(1)} <MiniStatusDot status={item.status} /></td>
                    <td className="px-4 py-3">{item.disposition}</td>
                    <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {item.measurement && (
                        <ItemActionButtons measurement={item.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* <Card className="border-[#E2E5EA]">
          <CardContent className="p-4">
            <SectionHead title="Calibration & equipment" />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
              {[
                ['Test method', 'AUT+TOFD', true],
                ['Accept. std', 'API 5L'],
                ['Accept. level', AUT_DETAIL.acceptanceLevel, true],
                ['Cal. block', AUT_DETAIL.calibration.block, true],
                ['Notch depth', '10 % WT', true],
                ['Equipment', 'GE Phasor XS PXS-32'],
                ['Operator', 'UT Level II - ASNT'],
              ].map(([label, value, mono]) => (
                <div key={String(label)}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
                  <div className={cn('mt-1 text-[12px] font-semibold text-blueprint-blue', mono && 'font-mono')}>{value}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card> */}
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', AUT_DETAIL.reportId, true],
          ['Performed at', formatDisplayDate(AUT_DETAIL.performedAt), true],
          ['Heat', heatNumber, true],
          ['Station', AUT_DETAIL.station],
          ['Operator', AUT_DETAIL.operator],
          ['Specification', AUT_DETAIL.spec],
          ['Source file', AUT_DETAIL.sourceFile, true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', frequency || 'per_pipe', true], ['Indications', indications.length, true], ['Status', <StatusBadge key="status" status={indications.some((item) => item.status === 'FAIL') ? 'FAIL' : 'PASS'} />]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
